import type { RealtimeEvent } from '@moussenaires/shared';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { useAuth } from '../context/AuthContext.js';
import { useToast } from '../context/ToastContext.js';

const RECONNECT_DELAYS_MS = [1000, 2000, 5000, 10_000];

/** Per-character live sync state, driven entirely by `roster.syncStarted`/`characterSynced` — read via this same key + a no-op queryFn from `RosterHeroWall`. */
export const ROSTER_SYNC_PROGRESS_KEY = ['roster', 'syncProgress'] as const;
export type RosterSyncProgress = Record<number, 'pending' | 'done'>;

/** How long a card's checkmark stays up before fading away on its own — matches the CSS animation's duration in RosterHeroWall.css. */
const SYNC_CHECK_VISIBLE_MS = 1800;

function tokenFromStorage(): string | null {
  try {
    return localStorage.getItem('moussenaires.token');
  } catch {
    return null;
  }
}

/**
 * Owns the single WebSocket connection for the session: reconnects with
 * backoff, invalidates the relevant react-query caches so open views update
 * live, and raises a toast for anything the user should notice right away.
 *
 * Opens even when signed out — an anonymous visitor can't be the target of
 * a per-user push, but they still need to see public broadcasts live, e.g.
 * the top-left sync button's cooldown timer.
 */
export function useRealtime(): void {
  const { isAuthenticated } = useAuth();
  const queryClient = useQueryClient();
  const { pushToast } = useToast();
  const attemptRef = useRef(0);
  const socketRef = useRef<WebSocket | null>(null);
  const closedByUsRef = useRef(false);

  useEffect(() => {
    const token = isAuthenticated ? tokenFromStorage() : null;

    closedByUsRef.current = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    function connect() {
      const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
      const query = token ? `?token=${encodeURIComponent(token)}` : '';
      const socket = new WebSocket(`${protocol}://${window.location.host}/ws${query}`);
      socketRef.current = socket;

      socket.addEventListener('open', () => {
        attemptRef.current = 0;
      });

      socket.addEventListener('message', (event) => {
        let parsed: RealtimeEvent;
        try {
          parsed = JSON.parse(event.data as string) as RealtimeEvent;
        } catch {
          return;
        }
        handleEvent(parsed, queryClient, pushToast);
      });

      socket.addEventListener('close', () => {
        if (closedByUsRef.current) return;
        const delay =
          RECONNECT_DELAYS_MS[Math.min(attemptRef.current, RECONNECT_DELAYS_MS.length - 1)];
        attemptRef.current += 1;
        timer = setTimeout(connect, delay);
      });
    }

    connect();

    return () => {
      closedByUsRef.current = true;
      if (timer) clearTimeout(timer);
      socketRef.current?.close();
    };
  }, [isAuthenticated, queryClient, pushToast]);
}

function handleEvent(
  event: RealtimeEvent,
  queryClient: ReturnType<typeof useQueryClient>,
  pushToast: ReturnType<typeof useToast>['pushToast'],
): void {
  switch (event.type) {
    case 'notification':
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
      pushToast({
        id: `notification-${event.notification.id}`,
        kind: event.notification.type,
        actor: event.notification.actor?.displayName ?? null,
        subject: event.notification.subject,
      });
      return;
    case 'post.created':
    case 'post.updated':
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      return;
    case 'post.deleted':
      void queryClient.invalidateQueries({ queryKey: ['feed'] });
      return;
    case 'comment.created':
      void queryClient.invalidateQueries({ queryKey: ['feed', 'post', event.comment.postId] });
      return;
    case 'comment.deleted':
      void queryClient.invalidateQueries({ queryKey: ['feed', 'post', event.postId] });
      return;
    case 'storage.updated':
      queryClient.setQueryData(['storage'], event.usage);
      return;
    case 'roster.syncStarted': {
      queryClient.setQueryData(['roster', 'syncStatus'], { cooldownUntil: event.cooldownUntil });
      const pending: RosterSyncProgress = {};
      for (const characterId of event.characterIds) pending[characterId] = 'pending';
      queryClient.setQueryData(ROSTER_SYNC_PROGRESS_KEY, pending);
      return;
    }
    case 'roster.characterSynced': {
      const characterId = event.characterId;
      queryClient.setQueryData(
        ROSTER_SYNC_PROGRESS_KEY,
        (current: RosterSyncProgress | undefined) => ({ ...current, [characterId]: 'done' }),
      );
      // The checkmark is a self-contained CSS animation, timed to fade to
      // nothing by SYNC_CHECK_VISIBLE_MS — this just removes the now-invisible
      // element afterwards rather than leaving it "done" forever.
      window.setTimeout(() => {
        queryClient.setQueryData(
          ROSTER_SYNC_PROGRESS_KEY,
          (current: RosterSyncProgress | undefined) => {
            if (!current || !(characterId in current)) return current;
            const next = { ...current };
            delete next[characterId];
            return next;
          },
        );
      }, SYNC_CHECK_VISIBLE_MS);
      return;
    }
    case 'roster.syncCompleted':
      void queryClient.invalidateQueries({ queryKey: ['roster'] });
      return;
    case 'ready':
      return;
  }
}
